// src/components/private/SecondaryNavBar.jsx

import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSecurity } from '../../SecurityContext';
import { NotificationBell } from "../NotificationBell";
import SecondaryNavFundingWidget from './SecondaryNavFundingWidget';
import SupportChoicePopup from '../donate/SupportChoicePopup';
import GuestAccessRequestsPopup from './GuestAccessRequestsPopup';
import { useWebsiteAccessGate } from '../../hooks/useWebsiteAccessGate';
import AltAccountSecondaryNotice from '../AltAccountSecondaryNotice';
import { useActiveUserIsAltAccount } from '../../hooks/useActiveUserIsAltAccount';

function SecondaryNavBar() {
  const { t } = useTranslation();
  const { uiHasPermission } = useSecurity();
  const { navUnlocked, statusLoading } = useWebsiteAccessGate();
  const { showAltAccountWarning } = useActiveUserIsAltAccount();
  const lastScrollRef = useRef(0);
  const [scrollVisible, setScrollVisible] = useState(true);

  useEffect(() => {
    if (statusLoading || !navUnlocked) {
      setScrollVisible(false);
      return undefined;
    }

    setScrollVisible(true);

    const handleScroll = () => {
      const currentScroll = window.pageYOffset;
      setScrollVisible(currentScroll <= 0 || currentScroll < lastScrollRef.current);
      lastScrollRef.current = currentScroll;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [navUnlocked, statusLoading]);

  if (statusLoading || !navUnlocked) {
    return <SupportChoicePopup />;
  }

  return (
    <div
      className="secondary-nav-container"
      data-resource="private"
      data-action="read"
      data-description="Private"
    >
      <div
        className={scrollVisible ? 'secondary-nav-bar show' : 'secondary-nav-bar hide'}
      >
        <nav
          className="secondary-menu"
          data-resource="private.navbar"
          data-action="read"
          data-description="Navbar"
        >
          <div className="secondary-menu-content">
            <div className="secondary-menu-inner">
              {uiHasPermission('private.navbar.personalhub', 'read') && (
                <NavLink
                  className={({ isActive }) => `nav2-link${isActive ? ' is-active' : ''}`}
                  to="/members/personalhub"
                  data-resource="private.navbar.personalhub"
                  data-action="read"
                  data-description="Navbar → Personal hub"
                >
                  <span className="nav2-link__label">{t('personalhub')}</span>
                </NavLink>
              )}

              {uiHasPermission('private.navbar.roster', 'read') && (
                <NavLink
                  className={({ isActive }) => `nav2-link${isActive ? ' is-active' : ''}`}
                  to="/members/Roster"
                  data-resource="private.navbar.roster"
                  data-action="read"
                  data-description="Navbar → Roster"
                >
                  <span className="nav2-link__label">{t('Roster')}</span>
                </NavLink>
              )}

              {uiHasPermission('private.navbar.calendar', 'read') && (
                <NavLink
                  className={({ isActive }) => `nav2-link${isActive ? ' is-active' : ''}`}
                  to="/members/calendar"
                  data-resource="private.navbar.calendar"
                  data-action="read"
                  data-description="Navbar → Calendar"
                >
                  <span className="nav2-link__label">{t('Calendar')}</span>
                </NavLink>
              )}

              {uiHasPermission('private.navbar.academy', 'read') && (
                <NavLink
                  className={({ isActive }) => `nav2-link${isActive ? ' is-active' : ''}`}
                  to="/members/academy"
                  data-resource="private.navbar.academy"
                  data-action="read"
                  data-description="Navbar → Academy"
                >
                  <span className="nav2-link__label">{t('Academy')}</span>
                </NavLink>
              )}

              {showAltAccountWarning ? (
                <AltAccountSecondaryNotice />
              ) : null}

              <div className="secondary-nav-utilities">
                <GuestAccessRequestsPopup />
                <SecondaryNavFundingWidget />
                <NotificationBell />
              </div>
            </div>
          </div>
        </nav>
      </div>
      <SupportChoicePopup />
    </div>
  );
}

export default SecondaryNavBar;
